(() => {
  function init(root) {
    if (root.dataset.searchReady) return;
    const search = root.querySelector('[data-sq-search]');
    const input = search?.querySelector('[data-sq-search-input]');
    const box = search?.querySelector('[data-sq-suggestions]');
    const status = search?.querySelector('[data-sq-search-status]');
    if (!input || !box || !status) return;
    root.dataset.searchReady = '1';
    const lifecycle = new AbortController();
    const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: lifecycle.signal });
    const cache = new Map();
    let timer, controller, version = 0, active = -1;
    const cancelPending = () => {
      version++;
      clearTimeout(timer);
      controller?.abort();
      controller = null;
      input.removeAttribute('aria-busy');
    };
    const collapse = () => {
      box.hidden = true;
      active = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      box.querySelectorAll('[data-sq-result]').forEach(link => link.setAttribute('aria-selected', 'false'));
    };
    const dismiss = () => {
      cancelPending();
      collapse();
      status.textContent = '';
    };
    const fullURL = query => {
      const url = new URL(search.dataset.endpoint, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return url;
    };
    const setActive = index => {
      const links = [...box.querySelectorAll('[data-sq-result]')];
      if (!links.length) return;
      active = (index + links.length) % links.length;
      links.forEach((link, i) => link.setAttribute('aria-selected', String(i === active)));
      input.setAttribute('aria-activedescendant', links[active].id);
      links[active].scrollIntoView({ block: 'nearest' });
    };
    const show = html => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-sq-search-results]');
      if (!content) throw new Error('Missing search results');
      content.querySelectorAll('script').forEach(script => script.remove());
      const total = Number(content.dataset.totalResults);
      box.replaceChildren(...content.childNodes);
      box.querySelectorAll('[data-sq-result]').forEach((link, i) => {
        link.id = box.id + '-option-' + i;
        link.tabIndex = -1;
        link.setAttribute('aria-selected', 'false');
      });
      active = -1;
      box.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
      status.textContent = total > 0
        ? search.dataset.resultsLabel.replace('__COUNT__', String(total))
        : search.dataset.emptyLabel;
    };
    const fallback = query => {
      const link = document.createElement('a');
      link.href = fullURL(query).href;
      link.textContent = search.dataset.noResults;
      link.id = box.id + '-option-0';
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.dataset.sqResult = '';
      link.tabIndex = -1;
      box.replaceChildren(link);
      active = -1;
      box.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
      status.textContent = search.dataset.errorLabel;
    };
    const request = async (query, sequence) => {
      if (sequence !== version || document.activeElement !== input) return;
      if (cache.has(query)) {
        show(cache.get(query));
        return;
      }
      const pending = new AbortController();
      controller = pending;
      const timeout = setTimeout(() => pending.abort(), 8000);
      input.setAttribute('aria-busy', 'true');
      status.textContent = search.dataset.loading;
      try {
        const url = fullURL(query);
        url.searchParams.set('section_id', 'sooqja-search-suggestions');
        const response = await fetch(url, { signal: pending.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search request failed');
        const html = await response.text();
        if (sequence !== version || document.activeElement !== input) return;
        show(html);
        cache.set(query, html);
        if (cache.size > 12) cache.delete(cache.keys().next().value);
      } catch (error) {
        if (sequence === version && document.activeElement === input) fallback(query);
      } finally {
        clearTimeout(timeout);
        if (sequence === version) {
          input.removeAttribute('aria-busy');
          controller = null;
        }
      }
    };
    const schedule = () => {
      dismiss();
      const query = input.value.trim();
      if (query.length < 2) return;
      const sequence = version;
      timer = setTimeout(() => request(query, sequence), 250);
    };
    listen(input, 'input', event => {
      if (event.isComposing) dismiss();
      else schedule();
    });
    listen(input, 'compositionend', schedule);
    listen(input, 'keydown', event => {
      if (event.isComposing) return;
      if (event.key === 'Tab') {
        dismiss();
        return;
      }
      if (event.key === 'Escape') {
        if (!box.hidden || input.hasAttribute('aria-busy')) {
          event.preventDefault();
          event.stopPropagation();
        }
        dismiss();
        return;
      }
      if (event.key === 'ArrowDown' && box.hidden && cache.has(input.value.trim())) show(cache.get(input.value.trim()));
      if (box.hidden) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(active < 0 ? (event.key === 'ArrowDown' ? 0 : -1) : active + (event.key === 'ArrowDown' ? 1 : -1));
      } else if (event.key === 'Enter' && active >= 0) {
        const link = box.querySelectorAll('[data-sq-result]')[active];
        if (link) {
          event.preventDefault();
          location.assign(link.href);
        }
      }
    });
    listen(search, 'focusout', event => {
      if (!search.contains(event.relatedTarget)) dismiss();
    });
    listen(document, 'pointerdown', event => {
      if (!search.contains(event.target)) dismiss();
    });
    listen(document, 'shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      dismiss();
      lifecycle.abort();
      delete root.dataset.searchReady;
    });
  }
  document.querySelectorAll('[data-sq-header]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-sq-header]').forEach(init));
})();
